import { waitFor } from '@testing-library/react';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError, ApiErrorResponse } from 'types/Error';

import { ObjectQueryState } from '../../types/Api';

/**
 * Creates a case-insensitive regular expression matcher from a given input string or number.
 *
 * This helper handles two common cases:
 *
 * 1. If the input is an UPPERCASE string with underscores (e.g., "IN_PROGRESS"),
 *    it converts it into a "Title Case" spaced string (e.g., "In Progress") and creates
 *    a regex to match that, ignoring case.
 *
 * 2. For all other inputs, it safely escapes any special regex characters
 *    to avoid unintended pattern matching and returns a case-insensitive regex.
 *
 * This allows flexible matching in UI tests where you want to match text that may
 * be formatted differently from raw constants or identifiers.
 *
 * @param {string | number} input - The input string or number to convert to a regex matcher.
 * @returns {RegExp} A case-insensitive RegExp for matching the input text appropriately.
 */
export function flexibleMatcher(input: string | number): RegExp {
    const str = String(input);

    // Helper: Escape regex special chars
    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Detect UPPERCASE_WITH_UNDERSCORES (only letters, underscores, all uppercase)
    const isUpperUnderscore = /^[A-Z_]+$/.test(str);

    if (isUpperUnderscore) {
        // Convert to "Title Case" with spaces instead of underscores
        const pretty = str
            .toLowerCase()
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        return new RegExp(pretty, 'i');
    } else {
        // Escape special regex chars for safe matching
        return new RegExp(escapeRegex(str), 'i');
    }
}

/**
 * Converts a human-readable label string into a safe `data-testid` string.
 * - Lowercases all characters
 * - Replaces spaces with dashes
 * - Removes any non-alphanumeric or non-dash characters
 *
 * Useful for consistently generating test IDs from component labels.
 */
export const labelToTestId = (label: string) =>
    label
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');

export const mockObjectQueryState = <T>(data?: T, overrides?: Partial<ObjectQueryState<T>>): ObjectQueryState<T> => ({
    data,
    isLoading: false,
    isError: false,
    isFetched: false,
    error: null,
    ...overrides
});

/**
 * Creates a mock AxiosError with the given message and status code.
 * Can be used in tests for React Query error scenarios.
 */
export const mockApiError = (message: string, statusCode = 400): ApiError => {
    const errorResponse: ApiErrorResponse = { message, statusCode };

    // Minimal AxiosError shape
    const axiosError: Partial<AxiosError<ApiErrorResponse>> = {
        isAxiosError: true,
        name: 'AxiosError',
        message,
        response: {
            data: errorResponse,
            status: statusCode,
            headers: {}, // must exist
            statusText: '',
            config: { headers: {} } as InternalAxiosRequestConfig
        },
        config: { headers: {} } as InternalAxiosRequestConfig,
        toJSON: () => ({})
    };

    return axiosError as AxiosError<ApiErrorResponse>;
};

/**
 * Waits for a DOM element to meet a specific expectation.
 * @param queryFn - a function that returns the element to check (e.g., () => screen.getByRole('button', { name: /Complete/i }))
 * @param assertionFn - a function that runs an assertion on the element (e.g., el => expect(el).toBeDisabled())
 * @param timeout - optional timeout in milliseconds (default 10_000)
 */
export async function waitForElementState<T extends HTMLElement>(
    queryFn: () => T,
    assertionFn: (el: T) => void,
    timeout = 10_000
) {
    await waitFor(
        () => {
            const el = queryFn();
            assertionFn(el);
        },
        { timeout }
    );
}
